import classNames from 'classnames';
import type { FC, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { copy } from 'utils/helpers';
import { DocumentDuplicateIconOutline } from 'utils/icons';
import type { ButtonComponentProps } from '../Button';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';

const Copy: FC<{ children: string }> = (props) => {
  const copy = useCallback(() => {
    navigator.clipboard.writeText(props.children);
    setCopied(true);
  }, [props.children]);

  const [copied, setCopied] = useState(false);

  const { t } = useTranslation();

  return (
    <div
      className={classNames('flex items-center gap-1', {
        'cursor-pointer': !copied,
      })}
      onClick={copy}
      onMouseLeave={() => setCopied(false)}
    >
      <DocumentDuplicateIconOutline className="w-4 h-4" />
      <div>{copied ? t('copied') : t('copy')}</div>
    </div>
  );
};

type CopyButtonProps = {
  textToCopy: string;
  render: (copied: boolean) => ReactNode;
  renderTooltip?: (copied: boolean) => ReactNode;
  tooltipStyle?: 'dark' | 'light' | 'white';
} & ButtonComponentProps;

export const CopyButton: FC<CopyButtonProps> = ({
  textToCopy,
  render,
  renderTooltip,
  tooltipStyle = 'dark',
  ...buttonProps
}) => {
  const [copied, setCopied] = useState(false);

  return (
    <Tooltip
      style={tooltipStyle}
      content={renderTooltip?.(copied)}
      hoverable={false}
    >
      <Button
        {...buttonProps}
        onClick={(e) => {
          copy(textToCopy).then((success) => {
            if (success) {
              setCopied(true);
            }
          });
          buttonProps.onClick?.(e);
        }}
        onMouseLeave={(e) => {
          setCopied(false);
          buttonProps.onMouseLeave?.(e);
        }}
      >
        {render(copied)}
      </Button>
    </Tooltip>
  );
};

export default Copy;
